<script setup lang="ts">
import { computed, ref } from 'vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useCitizenInquiriesStore } from '../stores/citizenInquiriesStore'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
  }>(),
  {
    modelValue: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const store = useCitizenInquiriesStore()
const feedbackStore = useFeedbackStore()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
})

const isCopied = ref(false)

const handleClose = () => {
  isOpen.value = false
}

const copyLink = async () => {
  if (!store.qrData?.url) return

  try {
    await navigator.clipboard.writeText(store.qrData.url)
    isCopied.value = true
    feedbackStore.showToast({
      type: 'success',
      message: 'Ссылка скопирована в буфер обмена',
      time: 3000,
    })
    setTimeout(() => {
      isCopied.value = false
    }, 2500)
  } catch (e) {
    console.error('Ошибка копирования ссылки:', e)
  }
}

const printQr = () => {
  window.print()
}

const downloadQrImage = () => {
  if (!store.qrData?.qr_code) return

  const link = document.createElement('a')
  link.href = store.qrData.qr_code
  link.download = 'appeals-qr-code.png'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="40rem"
    persistent
    content-class="entity-form-menu"
  >
    <VCard class="pa-6 d-flex flex-column gap-6">
      <div class="d-flex justify-space-between align-center">
        <span class="text-h4 font-weight-semibold">QR-код обращений граждан</span>
        <CustomCloseButton :close-action="handleClose" />
      </div>

      <!-- Контент QR-кода -->
      <div v-if="store.qrData" class="d-flex flex-column gap-6">
        <div class="d-flex justify-center">
          <div class="qr-preview-container pa-4 border rounded bg-surface">
            <VImg
              :src="store.qrData.qr_code"
              alt="QR-код обращений"
              width="220"
              height="220"
              class="qr-image"
            />
          </div>
        </div>

        <div class="d-flex flex-column gap-1 text-center">
          <span class="text-body-1 font-weight-medium">Публичная форма для пациентов</span>
          <span class="text-body-2 text-medium-emphasis">
            Отсканируйте камерой смартфона, чтобы оставить отзыв, жалобу или пожелание
          </span>
        </div>

        <!-- Кнопки действий: строго справа внизу -->
        <div class="d-flex align-center justify-end gap-3 flex-wrap">
          <VBtn
            variant="outlined"
            color="secondary"
            :prepend-icon="isCopied ? 'ri-check-line' : 'ri-file-copy-line'"
            @click="copyLink"
          >
            {{ isCopied ? 'Скопировано' : 'Скопировать ссылку' }}
          </VBtn>

          <VBtn
            variant="outlined"
            color="secondary"
            prepend-icon="ri-download-2-line"
            @click="downloadQrImage"
          >
            Скачать PNG
          </VBtn>

          <VBtn
            color="primary"
            class="general-button"
            prepend-icon="ri-printer-line"
            @click="printQr"
          >
            Печать
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Блок для печати (виден только на принтере, без кнопок и модалки) -->
  <Teleport to="body">
    <div v-if="store.qrData" class="citizen-qr-print-teleport">
      <div class="print-poster">
        <h1 class="print-title">ОБРАЩЕНИЯ И ОТЗЫВЫ ГРАЖДАН</h1>
        <p class="print-subtitle">Нам важно ваше мнение о качестве медицинских услуг</p>
        <div class="print-qr-wrapper">
          <img :src="store.qrData.qr_code" alt="QR-код обращений" class="print-qr-image" />
        </div>
        <p class="print-instructions">
          Наведите камеру смартфона на QR-код,<br />
          чтобы оставить обращение, жалобу или благодарность
        </p>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.qr-preview-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.qr-image {
  inline-size: 220px;
  block-size: 220px;
  object-fit: contain;
}
</style>

<style lang="scss">
@media screen {
  .citizen-qr-print-teleport {
    display: none !important;
  }
}

@media print {
  /* Убираем стандартные колонтитулы браузера (дату, URL, заголовок) */
  @page {
    size: auto;
    margin: 0;
  }

  /* Скрываем всё приложение и Vuetify-диалоги */
  body > *:not(.citizen-qr-print-teleport),
  .v-overlay-container,
  .v-overlay,
  .v-dialog,
  #app {
    display: none !important;
    visibility: hidden !important;
  }

  .citizen-qr-print-teleport {
    display: flex !important;
    position: fixed;
    inset: 0;
    justify-content: center;
    align-items: center;
    background: #ffffff !important;
    padding: 2cm;
    text-align: center;
    visibility: visible !important;
    z-index: 999999;
  }

  .print-poster {
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 3px solid #333333;
    border-radius: 16px;
    padding: 3rem 2rem;
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
    background: #ffffff !important;
  }

  .print-title {
    font-size: 26px;
    font-weight: 800;
    margin-bottom: 0.5rem;
    color: #111827;
  }

  .print-subtitle {
    font-size: 15px;
    color: #4b5563;
    margin-bottom: 1.5rem;
  }

  .print-qr-wrapper {
    padding: 1rem;
    background: #ffffff;
    margin-bottom: 1.5rem;
  }

  .print-qr-image {
    width: 260px;
    height: 260px;
    object-fit: contain;
  }

  .print-instructions {
    font-size: 15px;
    line-height: 1.5;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0;
  }
}
</style>
